import { useCallback } from 'react'
import { publishClipPlaneCreateEvent } from '@ui/events'
import { useControlStore } from '@editorStores'
import { useSelectionMode } from '@editorHooks'

const createImportHelperGroupId = () =>
  `import-helper-${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Returns a callback that creates the clip-plane "import helper": two linked z-plane clip planes,
 * one cutting the IFC/GLB model and one (dashed outline) cutting everything, so it is easy to see
 * which elements have already been imported. Shared by the clip-plane controls and the IFC import
 * tools section.
 */
const useCreateClipPlaneImportHelper = (): (() => void) => {
  const setPendingClipPlanePoints = useControlStore(state => state.setPendingClipPlanePoints)
  const { unsetSelectionMode } = useSelectionMode()

  return useCallback(() => {
    setPendingClipPlanePoints([])
    unsetSelectionMode()
    const linkGroupId = createImportHelperGroupId()

    publishClipPlaneCreateEvent({
      target: 'gltf',
      linkGroupId,
      normal: { x: 0, y: 0, z: -1 },
      point: { x: 0, y: 0, z: 1.5 },
    })
    publishClipPlaneCreateEvent({
      target: 'all',
      linkGroupId,
      renderMode: 'dashed-outline',
      normal: { x: 0, y: 0, z: -1 },
      point: { x: 0, y: 0, z: 2.5 },
    })
  }, [setPendingClipPlanePoints, unsetSelectionMode])
}

export default useCreateClipPlaneImportHelper
